import { CalendarDots, CaretDown } from '@phosphor-icons/react'
import { Popover, PopoverContent, PopoverTrigger, Icon } from '@shopflo/ui'
import { useState } from 'react'
import { format } from 'date-fns'
import { DayPicker } from 'react-day-picker'
import { Calendar } from './calendar'

interface DatePickerProps {
  date: Date | string
  onChange: (date: Date) => void
  disableFutureDates?: boolean
  disablePastDates?: boolean
  min?: Date
  max?: Date
  trigger?: React.ReactNode
}

export default function DatePicker({
  date,
  onChange,
  disableFutureDates = false,
  disablePastDates = false,
  min,
  max,
  trigger
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date(date))

  const handleSelect = (selected: Date | undefined) => {
    if (selected) {
      setSelectedDate(selected)
      onChange?.(selected)
      setOpen(false)
    }
  }

  const disabledMatchers: Parameters<typeof DayPicker>[0]['disabled'] = [
    ...(disablePastDates ? [{ before: new Date() }] : []),
    ...(disableFutureDates ? [{ after: new Date() }] : []),
    ...(min ? [{ before: min }] : []),
    ...(max ? [{ after: max }] : [])
  ]

  return (
    <div className='flex flex-col gap-6'>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {trigger ? (
            <button onClick={() => setOpen(!open)}>{trigger}</button>
          ) : (
            <button
              onClick={() => setOpen(!open)}
              className='flex w-[12rem] items-center justify-between rounded-lg border-input bg-white px-2 py-1.5 text-left text-text-sm-regular input-outline hover:bg-accent'
            >
              <div className='flex items-center gap-1'>
                <Icon IconComponent={CalendarDots} size='sm' iconProps={{ className: 'text-zinc-500' }} />
                <span className='text-text-sm-regular text-zinc-800'>
                  {selectedDate ? format(selectedDate, 'dd MMM yyyy') : 'Select date'}
                </span>
              </div>
              <CaretDown className='size-4 opacity-50' />
            </button>
          )}
        </PopoverTrigger>

        <PopoverContent align='start' className='w-auto overflow-hidden bg-white p-0  rounded-xl !border-none'>
          <Calendar
            mode='single'
            selected={selectedDate}
            onSelect={handleSelect}
            captionLayout='dropdown'
            disabled={disabledMatchers}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
